import { supabase } from "../utils/supabase";

/**
 * AuthService - A service layer for Supabase authentication
 */
class AuthService {
  // Current user state
  currentUser = null;

  // User profile data
  userProfile = null;

  // Auth state observers
  observers = [];

  // Profile observers
  profileObservers = [];

  constructor() {
    console.log("AuthService initialized");

    // Initialize auth state
    this.initAuthListener();

    // Get initial session - This needs to run on every page load
    this.checkAndSetSession();
  }

  /**
   * Check and set the current session
   */
  async checkAndSetSession() {
    console.log("Checking for existing session...");
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      console.error("Session error:", error);
      return;
    }

    const user = data?.session?.user || null;
    console.log("Session check result:", user ? "User found" : "No user found");

    this.currentUser = user;

    if (user) {
      // Fetch user profile
      await this.fetchUserProfile(user.id);
    }

    this.notifyObservers(user);
  }

  /**
   * Register a new user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} fullName - User's full name
   * @returns {Promise} - Auth provider's response
   */
  async registerWithEmailPassword(email, password, fullName = "") {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      if (data?.user) {
        await this.createOrUpdateUserProfile(data.user, {
          full_name: fullName,
        });
        return data.user;
      }

      return null;
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  }

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Auth provider's response
   */
  async signInWithEmailPassword(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return data.user;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  /**
   * Sign in with Google
   * @returns {Promise} - Auth provider's response
   */
  async signInWithGoogle() {
    try {
      // Get the actual hostname being used from the current URL
      const currentHostname = window.location.hostname;

      // Dynamic redirect URL based on the actual hostname
      let redirectUrl;
      if (currentHostname === "localhost" || currentHostname === "127.0.0.1") {
        redirectUrl = `${window.location.origin}/auth/callback`;
      } else if (currentHostname === "script.autogoldmine.com") {
        redirectUrl = `https://script.autogoldmine.com/auth/callback`;
      } else {
        // For any other hostnames, use the current origin as the base
        redirectUrl = `${window.location.origin}/auth/callback`;
      }

      console.log("Google Auth - Using redirect URL:", redirectUrl);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Google sign-in error:", error);
      throw error;
    }
  }

  /**
   * Sign out the current user
   * @returns {Promise} - Auth provider's response
   */
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  }

  /**
   * Initialize auth state listener
   */
  initAuthListener() {
    console.log("Setting up auth state listener");
    return supabase.auth.onAuthStateChange((_event, session) => {
      console.log("Auth state changed. Event:", _event);
      const user = session?.user || null;
      this.currentUser = user;
      this.notifyObservers(user);
    });
  }

  /**
   * Get the current user
   * @returns {Object|null} - Current user or null
   */
  async getCurrentUser() {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return data?.user || null;
    } catch (error) {
      console.error("Error getting user:", error);
      return null;
    }
  }

  /**
   * Add an observer to be notified of auth state changes
   * @param {Function} observer - Callback function receiving the user object
   */
  addAuthStateObserver(observer) {
    this.observers.push(observer);
    // Immediately notify with current state
    if (observer && typeof observer === "function") {
      observer(this.currentUser);
    }
  }

  /**
   * Remove an observer
   * @param {Function} observer - The observer to remove
   */
  removeAuthStateObserver(observer) {
    this.observers = this.observers.filter((obs) => obs !== observer);
  }

  /**
   * Notify all observers of an auth state change
   * @param {Object|null} user - The current user or null
   */
  notifyObservers(user) {
    this.observers.forEach((observer) => {
      if (observer && typeof observer === "function") {
        observer(user);
      }
    });
  }

  /**
   * Fetch user profile from Supabase
   * @param {string} userId - User ID
   * @returns {Promise} - User profile
   */
  async fetchUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error fetching user profile:", error);
        return null;
      }

      this.userProfile = data;
      this.notifyProfileObservers(data);
      return data;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  }

  /**
   * Get user's display name
   * @returns {string} - User's display name
   */
  getUserDisplayName() {
    // Try to get name from user profile
    if (this.userProfile?.full_name) {
      return this.userProfile.full_name;
    }

    // Fall back to metadata from auth
    if (this.currentUser?.user_metadata?.full_name) {
      return this.currentUser.user_metadata.full_name;
    }

    if (this.currentUser?.user_metadata?.name) {
      return this.currentUser.user_metadata.name;
    }

    // Last resort: use email or default
    return this.currentUser?.email?.split("@")[0] || "Distinguished Creator";
  }

  /**
   * Add an observer to be notified of profile changes
   * @param {Function} observer - Callback function receiving the profile object
   */
  addProfileObserver(observer) {
    this.profileObservers.push(observer);
    // Immediately notify with current state
    if (observer && typeof observer === "function") {
      observer(this.userProfile);
    }
  }

  /**
   * Remove a profile observer
   * @param {Function} observer - The observer to remove
   */
  removeProfileObserver(observer) {
    this.profileObservers = this.profileObservers.filter(
      (obs) => obs !== observer
    );
  }

  /**
   * Notify all profile observers of a change
   * @param {Object|null} profile - The current profile or null
   */
  notifyProfileObservers(profile) {
    this.profileObservers.forEach((observer) => {
      if (observer && typeof observer === "function") {
        observer(profile);
      }
    });
  }

  /**
   * Create or update a user profile in your Supabase database
   * @param {Object} user - The authenticated user
   * @param {Object} additionalData - Additional data to store in profile
   * @returns {Promise} - Supabase response
   */
  async createOrUpdateUserProfile(user, additionalData = {}) {
    if (!user) return null;

    try {
      // Create or update the user profile in the profiles table
      const profileData = {
        id: user.id,
        email: user.email,
        full_name:
          additionalData.full_name || user.user_metadata?.full_name || "",
        avatar_url:
          additionalData.avatar_url || user.user_metadata?.avatar_url || "",
        updated_at: new Date(),
      };

      const { data, error } = await supabase
        .from("profiles")
        .upsert(profileData, { onConflict: "id" });

      if (error) {
        console.error("Error updating profile:", error);
        return null;
      }

      // Update local profile data
      await this.fetchUserProfile(user.id);
      return data;
    } catch (error) {
      console.error("Error updating user profile:", error);
      // Don't throw here as this is secondary to authentication
      return null;
    }
  }

  /**
   * Reset password
   * @param {string} email - User email
   * @returns {Promise} - Supabase response
   */
  async resetPassword(email) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  }

  /**
   * Update user password
   * @param {string} newPassword - New password
   * @returns {Promise} - Supabase response
   */
  async updatePassword(newPassword) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Update password error:", error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;
